import { Stack } from "@mui/material";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "@privacy/translations";
import Typography from "@mui/material/Typography";
import { RiskIcon2, DpiaIcon3 } from "../../shared/icons";
import { Drawer } from "../../shared/Drawer";
import { DynamicGrid } from "../../dynamicGrid";
import { DynamicForm } from "../../dynamicForm";
import { useGetRiskById, useUpdateRiskAnswers, useGetRiskForm, useUpdateRiskDescription } from "../../api/risks";
import type { RiskById } from "../../api/risks.types";
import { RiskLevelLabel } from "../DpiaGridPage/RiskAssessment";
import { usePermission } from "../../shared/permissions";
import { usePrivacyNavigate } from "../../shared/usePrivacyNavigate";

export const RisksInlineDrawer = () => {
  const riskId = DynamicGrid.useSelectedRowId();
  const { navigate, paths } = usePrivacyNavigate();
  const riskQuery = useGetRiskById(riskId);
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const { mutateAsync, isError: isSaveError, isPending } = useUpdateRiskAnswers();
  const updateRiskDescriptionMutation = useUpdateRiskDescription();
  const riskFormQuery = useGetRiskForm();

  const isFormReadOnly = !hasPermission("nova-forms-api:dpia:create");

  return (
    <DynamicGrid.Drawer>
      {(() => {
        if (!riskQuery.data || !riskFormQuery.data) return <Drawer.Loading />;

        const risk = riskQuery.data;
        return (
          <DynamicForm.Provider
            onSave={answer => mutateAsync({ riskId: risk.id, answers: [answer] })}
            isSaving={isPending || updateRiskDescriptionMutation.isPending}
            isReadOnly={isFormReadOnly}
            isSaveError={isSaveError || updateRiskDescriptionMutation.isError}
            key={risk.id}
            answers={risk.answers}
          >
            {({ formState }) => (
              <>
                <Drawer.Header
                  status={DynamicForm.formStateToDrawerHeaderStatus(formState)}
                  title={risk.name}
                  renderIcon={props => <RiskIcon2 {...props} />}
                  onClose={() => navigate(paths.risksGrid())}
                />
                <Drawer.Content sx={{ py: 3 }}>
                  <DynamicForm.InlineFieldPrimary
                    label={t("Risks.Dpia")}
                    htmlFor="dpia"
                    input={
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <DpiaIcon3 />
                        <Typography sx={{ fontSize: theme => theme.typography.pxToRem(14) }}>
                          {risk.dpia.name}
                        </Typography>
                      </Stack>
                    }
                  />
                  <DynamicForm.InlineFieldPrimary
                    label={t("Risks.RiskLevel")}
                    htmlFor="riskLevel"
                    input={
                      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                        <RiskLevelLabel
                          label={risk.riskLevel.riskLevelLabel}
                          color={risk.riskLevel.riskLevelColor}
                          backgroundColor={risk.riskLevel.riskLevelBackgroundColor}
                        />
                      </Stack>
                    }
                  />
                </Drawer.Content>
                <Drawer.Divider />
                <Drawer.Content>
                  <DescriptionSection
                    description={risk.description}
                    risk={risk}
                    updateRiskDescriptionMutation={updateRiskDescriptionMutation}
                  />
                  <DynamicForm.InlineForm id={risk.id} form={riskFormQuery.data} />
                </Drawer.Content>
              </>
            )}
          </DynamicForm.Provider>
        );
      })()}
    </DynamicGrid.Drawer>
  );
};

const DescriptionSection: FC<{
  description: string;
  risk: RiskById;
  updateRiskDescriptionMutation: ReturnType<typeof useUpdateRiskDescription>;
}> = ({ description, risk, updateRiskDescriptionMutation }) => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const form = useForm({ defaultValues: { description: description ?? "" } });
  const isReadOnly = !hasPermission("nova-forms-api:dpia:create");
  return (
    <DynamicForm.InlineFormSection title={t("Risks.RiskForm.Title")}>
      <DynamicForm.InlineFieldPrimary
        label={t("Risks.Description")}
        htmlFor="description"
        infoPopoverContent={{
          title: t("Risks.Info.Header"),
          body: t("Risks.Info.Body"),
        }}
        input={
          <DynamicForm.StyledInlineTextInput
            onSave={updatedDescription => {
              return updateRiskDescriptionMutation.mutateAsync(
                {
                  id: risk.id,
                  dpiaId: risk.dpia.id,
                  name: risk.name,
                  description: updatedDescription,
                },
                { onSuccess: () => form.reset({ description: updatedDescription }) }
              );
            }}
            validation={{ validator: value => value.trim().length > 0 }}
            form={form}
            numberOfRows={3}
            id="description"
            isReadOnly={isReadOnly}
            renderErrorMessage={({ isSaving, isValidationError, isSaveError }) => (
              <>
                {!isSaving && isSaveError && <DynamicForm.ErrorMessage />}
                {isValidationError && (
                  <DynamicForm.ErrorMessage errorMessage={t("OptionCategoryGrid.DescriptionRequired")} />
                )}
              </>
            )}
          />
        }
      />
    </DynamicForm.InlineFormSection>
  );
};
